import * as THREE from "three";
import { getViewerHandles } from "../runtime/viewerHandles";
import { logger } from "../utils/logger";

type AutofocusTarget = {
  bodyUuid?: string | null;
  meshUuid?: string | null;
  collisionGeomUuid?: string | null;
  jointUuid?: string | null;
  siteUuid?: string | null;
  inertialUuid?: string | null;
  framing?: "recenter" | "fit";
  durationMs?: number;
};

type OrbitControlsLike = {
  minDistance?: number;
  maxDistance?: number;
};
type SceneObjectsGlobal = typeof window & {
  sceneObjectsRef?: { current?: Record<string, THREE.Object3D | null | undefined> };
};
type MeshLikeObject = THREE.Object3D & {
  isMesh?: boolean;
  geometry?: THREE.BufferGeometry;
};

let activeAnimationFrame: number | null = null;
const DEFAULT_CAMERA_DIRECTION = new THREE.Vector3(0.6, 0.5, 1).normalize();
const FOCUS_DISTANCE_PADDING = 1.35;

const resolveCollisionObject = (
  scene: THREE.Object3D,
  bodyUuid?: string | null,
  collisionGeomUuid?: string | null
) => {
  if (!collisionGeomUuid) return null;
  const key = String(collisionGeomUuid);
  const sep = key.indexOf(":");
  const bodyFromKey = sep > 0 ? key.slice(0, sep) : null;
  const geomFromKey = sep > 0 ? key.slice(sep + 1) : key;
  let target: THREE.Object3D | null = null;
  scene.traverse((obj) => {
    if (target) return;
    if (obj?.userData?.type !== "collision") return;
    const candidateBodyUuid = String(obj.userData?.bodyUuid ?? "");
    const expectedBodyUuid = bodyUuid || bodyFromKey;
    if (expectedBodyUuid && candidateBodyUuid !== String(expectedBodyUuid)) return;
    if (String(obj.userData?.geomUuid ?? "") !== geomFromKey) return;
    target = obj;
  });
  return target;
};

const resolveMeshObject = (scene: THREE.Object3D, meshUuid?: string | null) => {
  if (!meshUuid) return null;
  const byName = scene.getObjectByName(meshUuid);
  if (byName) return byName;
  let byUserData: THREE.Object3D | null = null;
  scene.traverse((obj) => {
    if (byUserData) return;
    if (String(obj?.userData?.meshName ?? "") === String(meshUuid)) {
      byUserData = obj;
    }
  });
  return byUserData;
};

const resolveBodyObject = (scene: THREE.Object3D, bodyUuid?: string | null) => {
  if (!bodyUuid) return null;
  const fromSceneObjectsRef =
    (window as SceneObjectsGlobal).sceneObjectsRef?.current?.[bodyUuid] || null;
  if (fromSceneObjectsRef) return fromSceneObjectsRef;
  let found: THREE.Object3D | null = null;
  scene.traverse((obj) => {
    if (found) return;
    if (String(obj?.userData?.bodyUuid ?? "") === String(bodyUuid)) {
      found = obj;
    }
  });
  return found;
};

const resolveJointObject = (scene: THREE.Object3D, jointUuid?: string | null) => {
  if (!jointUuid) return null;
  let found: THREE.Object3D | null = null;
  scene.traverse((obj) => {
    if (found) return;
    if (String(obj?.userData?.jointUuid ?? "") === String(jointUuid)) {
      found = obj;
    }
  });
  return found;
};

const resolveSiteObject = (scene: THREE.Object3D, siteUuid?: string | null) => {
  if (!siteUuid) return null;
  let found: THREE.Object3D | null = null;
  scene.traverse((obj) => {
    if (found) return;
    if (obj?.userData?.type !== "site") return;
    if (String(obj.userData?.siteUuid ?? "") === String(siteUuid)) {
      found = obj;
    }
  });
  return found;
};

const resolveInertialObject = (scene: THREE.Object3D, inertialUuid?: string | null) => {
  if (!inertialUuid) return null;
  const key = String(inertialUuid);
  const sep = key.indexOf(":");
  const bodyFromKey = sep > 0 ? key.slice(0, sep) : null;
  const inertialFromKey = sep > 0 ? key.slice(sep + 1) : key;
  let found: THREE.Object3D | null = null;
  scene.traverse((obj) => {
    if (found) return;
    const ud = obj?.userData;
    if (!ud) return;
    if (bodyFromKey && String(ud.bodyUuid ?? "") !== bodyFromKey) return;
    const candidateId = ud.inertialUuid || ud.inertialName;
    if (
      String(candidateId ?? "") === inertialFromKey ||
      String(obj.name ?? "") === inertialFromKey
    ) {
      found = obj;
    }
  });
  return found;
};

const isVisualAutofocusMesh = (object: THREE.Object3D) => {
  const maybeMesh = object as MeshLikeObject;
  if (!maybeMesh.isMesh || !maybeMesh.geometry) return false;
  let current: THREE.Object3D | null = object;
  while (current) {
    const type = current.userData?.type;
    if (type === "mesh" || type === "visual") return true;
    if (
      type === "collision" ||
      type === "inertial" ||
      type === "site" ||
      current.userData?.isJointVizGroup ||
      current.userData?.isJointArc ||
      current.userData?.isJointEndpoint ||
      current.userData?.isJointPivot ||
      current.userData?.isGlow ||
      current.userData?.isInertialHelper
    ) {
      return false;
    }
    current = current.parent;
  }
  return false;
};

const getVisualTargetBounds = (object: THREE.Object3D) => {
  const bounds = new THREE.Box3();
  const childBounds = new THREE.Box3();
  let hasVisualBounds = false;

  object.updateWorldMatrix?.(true, true);
  object.traverse((child) => {
    if (!isVisualAutofocusMesh(child)) return;
    childBounds.setFromObject(child);
    if (childBounds.isEmpty()) return;
    bounds.union(childBounds);
    hasVisualBounds = true;
  });

  return hasVisualBounds && !bounds.isEmpty() ? bounds : null;
};

const getBodyVisualBounds = (scene: THREE.Object3D, bodyUuid?: string | null) => {
  if (!bodyUuid) return null;
  const bounds = new THREE.Box3();
  const childBounds = new THREE.Box3();
  let hasVisualBounds = false;

  scene.updateWorldMatrix?.(true, true);
  scene.traverse((child) => {
    if (!isVisualAutofocusMesh(child)) return;
    let current: THREE.Object3D | null = child;
    let belongsToBody = false;
    while (current) {
      if (String(current.userData?.bodyUuid ?? "") === String(bodyUuid)) {
        belongsToBody = true;
        break;
      }
      current = current.parent;
    }
    if (!belongsToBody) return;
    childBounds.setFromObject(child);
    if (childBounds.isEmpty()) return;
    bounds.union(childBounds);
    hasVisualBounds = true;
  });

  return hasVisualBounds && !bounds.isEmpty() ? bounds : null;
};

const getTargetBounds = (
  object: THREE.Object3D,
  visualOnly = false,
  scene?: THREE.Object3D,
  bodyUuid?: string | null
) => {
  if (visualOnly) {
    const visualBounds = getVisualTargetBounds(object);
    if (visualBounds) return visualBounds;
    const owningBodyUuid = bodyUuid || object.userData?.bodyUuid || null;
    if (scene && owningBodyUuid) {
      const bodyVisualBounds = getBodyVisualBounds(scene, String(owningBodyUuid));
      if (bodyVisualBounds) return bodyVisualBounds;
    }
  }
  const bounds = new THREE.Box3().setFromObject(object);
  return bounds.isEmpty() ? null : bounds;
};

const getTargetPosition = (object: THREE.Object3D, bounds: THREE.Box3 | null) => {
  const targetPosition = new THREE.Vector3();
  if (bounds) {
    bounds.getCenter(targetPosition);
  } else {
    object.getWorldPosition(targetPosition);
  }
  return targetPosition;
};

const getFitDistance = (camera: THREE.PerspectiveCamera, bounds: THREE.Box3 | null) => {
  if (!bounds) return null;
  const sphere = new THREE.Sphere();
  bounds.getBoundingSphere(sphere);
  if (!Number.isFinite(sphere.radius) || sphere.radius <= 0) return null;

  const verticalFov = THREE.MathUtils.degToRad(camera.fov || 75);
  const aspect = Number.isFinite(camera.aspect) && camera.aspect > 0 ? camera.aspect : 1;
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * aspect);
  const limitingFov = Math.max(THREE.MathUtils.degToRad(1), Math.min(verticalFov, horizontalFov));
  const fitDistance = (sphere.radius / Math.sin(limitingFov / 2)) * FOCUS_DISTANCE_PADDING;
  return Number.isFinite(fitDistance) && fitDistance > 0 ? fitDistance : null;
};

const clampOrbitDistance = (
  controls: OrbitControlsLike,
  camera: THREE.PerspectiveCamera,
  distance: number
) => {
  let clamped = Math.max(distance, (camera.near || 0.01) * 8, 0.05);
  const minDistance = Number(controls?.minDistance);
  const maxDistance = Number(controls?.maxDistance);
  if (Number.isFinite(minDistance) && minDistance > 0) {
    clamped = Math.max(clamped, minDistance);
  }
  if (Number.isFinite(maxDistance) && maxDistance > 0) {
    clamped = Math.min(clamped, maxDistance);
  }
  return clamped;
};

export const autofocusCameraToSelection = ({
  bodyUuid = null,
  meshUuid = null,
  collisionGeomUuid = null,
  jointUuid = null,
  siteUuid = null,
  inertialUuid = null,
  framing = "recenter",
  durationMs = 600,
}: AutofocusTarget): boolean => {
  try {
    const { scene, controls, camera } = getViewerHandles();
    if (!scene || !controls || !camera) return false;

    const targetObject =
      resolveCollisionObject(scene, bodyUuid, collisionGeomUuid) ||
      resolveMeshObject(scene, meshUuid) ||
      resolveJointObject(scene, jointUuid) ||
      resolveSiteObject(scene, siteUuid) ||
      resolveInertialObject(scene, inertialUuid) ||
      resolveBodyObject(scene, bodyUuid);
    if (!targetObject) return false;

    const targetBounds =
      framing === "fit" ? getTargetBounds(targetObject, true, scene, bodyUuid) : null;
    const targetPosition = getTargetPosition(targetObject, targetBounds);

    if (activeAnimationFrame) {
      cancelAnimationFrame(activeAnimationFrame);
      activeAnimationFrame = null;
    }

    const startTarget = controls.target.clone();
    const startCameraPosition = camera.position.clone();
    const cameraOffset = new THREE.Vector3().subVectors(startCameraPosition, startTarget);
    const fitDistance = framing === "fit" ? getFitDistance(camera, targetBounds) : null;
    const destinationCameraPosition =
      fitDistance != null
        ? new THREE.Vector3()
            .copy(targetPosition)
            .addScaledVector(
              cameraOffset.lengthSq() > 0
                ? cameraOffset.clone().normalize()
                : DEFAULT_CAMERA_DIRECTION,
              clampOrbitDistance(controls, camera, fitDistance)
            )
        : new THREE.Vector3().addVectors(targetPosition, cameraOffset);

    if (durationMs <= 0) {
      controls.target.copy(targetPosition);
      camera.position.copy(destinationCameraPosition);
      controls.update();
      return true;
    }

    const startTime = Date.now();

    const step = () => {
      const progress = Math.min((Date.now() - startTime) / durationMs, 1);
      const ease = -(Math.cos(Math.PI * progress) - 1) / 2;
      controls.target.lerpVectors(startTarget, targetPosition, ease);
      camera.position.lerpVectors(startCameraPosition, destinationCameraPosition, ease);
      controls.update();
      if (progress < 1) {
        activeAnimationFrame = requestAnimationFrame(step);
      } else {
        activeAnimationFrame = null;
      }
    };

    activeAnimationFrame = requestAnimationFrame(step);
    return true;
  } catch (error) {
    logger.warn("[cameraAutofocus] Failed to autofocus camera to selection", error);
    return false;
  }
};
